import { type Measure, type MeasureDataByEntity } from '../../../api/work-periods-client/work-periods-client.type';
import { round } from '../../../helpers/math-helpers/math-helpers';
import type { AggregationType } from './overview.type';

/**
 * Formats KPI values - rounds floats to 2 decimal places, leaves whole numbers as is
 *
 * @param {number} value - The value to format
 * @returns {number} Formatted value
 */
const formatKPIValue = (value: number): number => {
  if (Number.isInteger(value)) {
    return value;
  }

  return round(value, 2);
};

/**
 * Aggregates measure values across all entities and time periods
 *
 * @param {MeasureDataByEntity | null | undefined} entityData - Data for all entities (entityId -> measureName -> date -> value)
 * @param {Measure} measure - The measure to aggregate
 * @param {AggregationType} aggregationType - How to aggregate: 'sum' or 'average'
 * @returns {number} Aggregated value across all entities
 */
const aggregateMeasureValueAcrossEntities = (
  entityData: MeasureDataByEntity | null | undefined,
  measure: Measure,
  aggregationType: AggregationType,
): number => {
  if (!entityData) {
    return 0;
  }

  const allValues: number[] = [];

  for (const entityId of Object.keys(entityData)) {
    const measureValues = entityData[entityId]?.[measure];
    if (measureValues) {
      const values = Object.values(measureValues).filter((v) => v !== null && v !== undefined);
      allValues.push(...values);
    }
  }

  if (allValues.length === 0) {
    return 0;
  }

  switch (aggregationType) {
    case 'sum':
      return allValues.reduce((acc, val) => acc + val, 0);
    case 'average': {
      const sum = allValues.reduce((acc, val) => acc + val, 0);
      return sum / allValues.length;
    }
    default:
      return 0;
  }
};

export { aggregateMeasureValueAcrossEntities, formatKPIValue };
